import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import {
  LayoutDashboard, Users, ShoppingBag, Hammer, Factory, Boxes, Wallet,
  FileBarChart, Search, Sun, Moon, LogOut, Menu, X, Landmark, KeyRound, Loader2, Package, Settings as SettingsIcon,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth, useTheme } from "@/App";
import api from "@/lib/api";
import { cn } from "@/lib/utils";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard },
  { to: "/customers", label: "Customers", icon: Users },
  { to: "/orders", label: "Orders", icon: ShoppingBag },
  { to: "/products", label: "Products", icon: Package },
  { to: "/karigars", label: "Karigars", icon: Hammer },
  { to: "/production", label: "Shop Production", icon: Factory },
  { to: "/stock", label: "Shop Stock", icon: Boxes },
  { to: "/expenses", label: "Expenses", icon: Wallet },
  { to: "/reports", label: "Reports", icon: FileBarChart },
  { to: "/accounts", label: "Business Accounts", icon: Landmark },
  { to: "/settings", label: "Settings", icon: SettingsIcon },
];

const GlobalSearch = () => {
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const ref = useRef(null);

  useEffect(() => {
    if (!q.trim()) { setResults(null); setOpen(false); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/search", { params: { q } });
        setResults(data);
        setOpen(true);
      } catch (err) {
        console.error("Global search failed", err);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const go = (path) => { setOpen(false); setQ(""); navigate(path); };
  const has = results && (results.customers.length || results.orders.length || results.karigars.length || results.productions.length);

  return (
    <div className="relative w-full max-w-md" ref={ref}>
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
      <Input
        data-testid="global-search-input"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Search customers, orders, designs, karigars..."
        className="pl-9 rounded-full bg-muted/50 border-transparent focus:border-border h-9"
      />
      {open && results && (
        <div data-testid="global-search-results" className="absolute top-11 left-0 right-0 z-50 rounded-xl border bg-popover shadow-lg overflow-hidden max-h-96 overflow-y-auto">
          {!has && <p className="p-4 text-sm text-muted-foreground">No results found</p>}
          {results.customers.map((c) => {
            const dup = results.customers.filter((x) => (x.name || "").trim().toLowerCase() === (c.name || "").trim().toLowerCase()).length > 1;
            return (
              <button key={c.id} onClick={() => go(`/customers/${c.id}`)} className="w-full text-left px-4 py-2.5 hover:bg-muted transition-colors text-sm">
                <span className="text-xs uppercase tracking-widest text-muted-foreground mr-2">Customer</span>{c.name}{dup && c.address ? <span className="text-amber-500"> — {c.address}</span> : null} {c.phone && <span className="text-muted-foreground">· {c.phone}</span>}
              </button>
            );
          })}
          {results.orders.map((o) => (
            <button key={o.id} onClick={() => go(`/orders/${o.id}`)} className="w-full text-left px-4 py-2.5 hover:bg-muted transition-colors text-sm">
              <span className="text-xs uppercase tracking-widest text-muted-foreground mr-2">Order</span>{o.order_number} · {o.customer_name}
            </button>
          ))}
          {results.karigars.map((k) => (
            <button key={k.id} onClick={() => go(`/karigars/${k.id}`)} className="w-full text-left px-4 py-2.5 hover:bg-muted transition-colors text-sm">
              <span className="text-xs uppercase tracking-widest text-muted-foreground mr-2">Karigar</span>{k.name}
            </button>
          ))}
          {results.productions.map((p) => (
            <button key={p.id} onClick={() => go("/production")} className="w-full text-left px-4 py-2.5 hover:bg-muted transition-colors text-sm">
              <span className="text-xs uppercase tracking-widest text-muted-foreground mr-2">Design</span>{p.design_name} {p.design_code && `(${p.design_code})`}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const NavLinks = ({ onNavigate }) => {
  const location = useLocation();
  return (
    <nav className="flex-1 space-y-1 px-3">
      {NAV.map(({ to, label, icon: Icon }) => {
        const active = to === "/" ? location.pathname === "/" : location.pathname.startsWith(to);
        return (
          <Link
            key={to}
            to={to}
            onClick={onNavigate}
            data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
            className={cn(
              "flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors",
              active ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted hover:text-foreground"
            )}
          >
            <Icon className="h-4 w-4" />
            {label}
          </Link>
        );
      })}
    </nav>
  );
};

const ChangePasswordDialog = ({ open, onOpenChange }) => {
  const [form, setForm] = useState({ current: "", next: "", confirm: "" });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.next !== form.confirm) { toast.error("New passwords do not match"); return; }
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: form.current, new_password: form.next });
      toast.success("Password changed");
      setForm({ current: "", next: "", confirm: "" });
      onOpenChange(false);
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Failed to change password");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-sm" data-testid="change-password-dialog">
        <DialogHeader><DialogTitle>Change Password</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">Current Password</Label>
            <Input data-testid="current-password-input" type="password" required value={form.current}
              onChange={(e) => setForm({ ...form, current: e.target.value })} className="h-11 rounded-lg" />
          </div>
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">New Password</Label>
            <Input data-testid="new-password-input" type="password" required minLength={6} value={form.next}
              onChange={(e) => setForm({ ...form, next: e.target.value })} className="h-11 rounded-lg" />
          </div>
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">Confirm New Password</Label>
            <Input data-testid="confirm-password-input" type="password" required minLength={6} value={form.confirm}
              onChange={(e) => setForm({ ...form, confirm: e.target.value })} className="h-11 rounded-lg" />
          </div>
          <Button data-testid="change-password-submit" type="submit" disabled={saving} className="w-full h-11 rounded-lg">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Update Password"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default function Layout({ children }) {
  const { logout, user } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);

  const Brand = (
    <div className="flex flex-col gap-1 px-6 py-6">
      <img src="/logo.png" alt="Anamta Noor" data-testid="brand-logo" className="h-12 w-auto self-start" />
      <p className="text-xs text-muted-foreground">Zari Handwork ERP</p>
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-64 flex-col border-r bg-card z-40">
        {Brand}
        <NavLinks />
        <div className="p-4 border-t space-y-2">
          <p className="text-xs text-muted-foreground truncate mb-2 px-2">{user?.email}</p>
          <Button data-testid="change-password-button" variant="outline" size="sm" className="w-full rounded-lg" onClick={() => setPwOpen(true)}>
            <KeyRound className="h-4 w-4 mr-2" /> Change Password
          </Button>
          <Button data-testid="logout-button" variant="outline" size="sm" className="w-full rounded-lg" onClick={logout}>
            <LogOut className="h-4 w-4 mr-2" /> Logout
          </Button>
        </div>
      </aside>

      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="fixed inset-0 bg-black/60" onClick={() => setMobileOpen(false)} />
          <div className="relative w-72 bg-card border-r flex flex-col h-full">
            <div className="flex items-center justify-between pr-3">
              {Brand}
              <Button variant="ghost" size="icon" data-testid="mobile-menu-close" onClick={() => setMobileOpen(false)}>
                <X className="h-5 w-5" />
              </Button>
            </div>
            <NavLinks onNavigate={() => setMobileOpen(false)} />
            <div className="p-4 border-t space-y-2">
              <Button data-testid="mobile-change-password-button" variant="outline" size="sm" className="w-full rounded-lg" onClick={() => { setMobileOpen(false); setPwOpen(true); }}>
                <KeyRound className="h-4 w-4 mr-2" /> Change Password
              </Button>
              <Button data-testid="mobile-logout-button" variant="outline" size="sm" className="w-full rounded-lg" onClick={logout}>
                <LogOut className="h-4 w-4 mr-2" /> Logout
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="lg:pl-64">
        <header className="sticky top-0 z-30 flex items-center gap-3 border-b backdrop-blur-xl bg-background/70 px-4 lg:px-8 h-16">
          <Button variant="ghost" size="icon" className="lg:hidden" data-testid="mobile-menu-button" onClick={() => setMobileOpen(true)}>
            <Menu className="h-5 w-5" />
          </Button>
          <GlobalSearch />
          <div className="ml-auto">
            <Button data-testid="theme-toggle" variant="ghost" size="icon" className="rounded-full" onClick={toggleTheme}>
              {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </Button>
          </div>
        </header>
        <main className="p-4 lg:p-8">{children}</main>
      </div>
      <ChangePasswordDialog open={pwOpen} onOpenChange={setPwOpen} />
    </div>
  );
}
